import React from 'react';
import { useIntl } from 'react-intl';
import { useField, UseFieldConfig } from 'react-final-form';

import Civilities from 'constants/civilities';
import { required, validateEmail } from 'utils/formUtils';

import { isEqualPhoneNumber } from 'modules/selfServices/types/SelfService';

import FormInput from 'components/ui/form/FormInput';
import PhoneNumberInput from 'components/ui/PhoneNumberInput';

import LanguageInput from './LanguageInput';
import CivilityInput from './CivilityInput';
import { CustomerInfo } from 'modules/selfServices/types/CustomerInfo';
import { getIn } from 'final-form';

const fieldConfig: UseFieldConfig<string> = { subscription: { value: true } };

export interface ContactFormProps {
  onError?: (hasError: boolean) => void;
  isPhoneNumberOrEmailMandatory?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({ onError, isPhoneNumberOrEmailMandatory }) => {
  const intl = useIntl();

  const { value: civility } = useField('civility', fieldConfig).input;
  const phoneNumberInput = useField('phoneNumber', {
    ...fieldConfig,
    isEqual: isEqualPhoneNumber,
  }).input;
  const { value: email } = useField('email', fieldConfig).input;

  const hasPhoneNumber = phoneNumberInput.value?.local?.trim().length > 0;
  const hasEmail = email?.trim().length > 0;

  const isCompany = civility === Civilities.COMPANY;
  const isOtherCivility = civility === Civilities.NONE;

  const onPhoneNumberError = React.useCallback((error: boolean) => {
    onError?.(error);
  }, [onError]);

  const validateEmailInput = React.useCallback((email: string, allValues: CustomerInfo) => {
    const phoneNumberLocal = getIn(allValues, 'phoneNumber.local');
    return validateEmail(email) || isPhoneNumberOrEmailMandatory && (phoneNumberLocal?.length > 0 ? undefined : required(email));
  }, [ isPhoneNumberOrEmailMandatory]);

  return (
    <div className="space-y-4 mb-4">
      <LanguageInput />
      <CivilityInput />
      {isCompany && (
        <FormInput
          label={intl.formatMessage({ id: 'customerInfo.companyName', defaultMessage: 'Company name' })}
          name="companyName"
          required
          validate={required}
          data-testid="companyName-input"
        />
      )}
      {!isCompany && (
        <>
          <FormInput
            label={intl.formatMessage({ id: 'customerInfo.firstName', defaultMessage: 'First name' })}
            name="firstName"
            data-testid="firstName-input"
            autoComplete="given-name"
          />
          <FormInput
            key={`${civility}_lastName`}
            label={intl.formatMessage({ id: 'customerInfo.lastName', defaultMessage: 'Last name' })}
            name="lastName"
            required={!isOtherCivility}
            validate={!isOtherCivility ? required : undefined}
            data-testid="lastName-input"
            autoComplete="family-name"
          />
        </>
      )}
      <PhoneNumberInput
        onError={onPhoneNumberError}
        required={isPhoneNumberOrEmailMandatory && !hasEmail}
        {...phoneNumberInput}
      />
      <FormInput
        label={intl.formatMessage({ id: 'customerInfo.email', defaultMessage: 'Email' })}
        name="email"
        validate={validateEmailInput}
        required={isPhoneNumberOrEmailMandatory && !hasPhoneNumber}
        data-testid="email-input"
        type="email"
        inputMode="email"
        autoComplete="email"
      />
    </div>
  );
};
export default ContactForm;
